import { PropsWithChildren, FC, useEffect } from 'react'
import { Checkbox, Select, Row, Divider, Col, Spin, InputNumber } from 'antd'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { EnterOutlined } from '@ant-design/icons'
import { StatusTag } from '@ProdWb/components/StatusTag'
import { formatDate } from '@ProdWb/logic'
import { useSelector } from 'react-redux'
import { isUserQCManager } from '@OpsWb/logic'

import Box from '~/core/Box'
import Text from '~/core/Text'
import { EXCEPTION_FORM_FIELDS } from '../constants'
import { DeliveryReceiptPropsType, AffectedLinesPropsType } from '../types'
import style from '../styles.css'
import { ExceptionCreator, ExceptionFailureType } from '~/gql/graphql'

import componentStyle from './styles.css'
const { Option } = Select

export const ReceiptDOFormField: FC<
    PropsWithChildren<DeliveryReceiptPropsType>
> = props => {
    const { fieldDecorator, receipts, deliveryOrders, exception } = props
    const getStatusTag = (status: string | null) => ({
        color: '',
        title: status || '',
    })
    const isQCManager = useSelector(isUserQCManager)

    return (
        <>
            <Box>
                <EnterOutlined className={componentStyle.enterIconStyle} />
                <Text className={componentStyle.receiptTitleStyle}>
                    {receipts.isQC ? 'QC:' : 'Receipt:'}
                </Text>
                <Form.Item
                    style={{
                        width: `${receipts.isQC ? '92%' : '88%'}`,
                        marginLeft: '8px',
                    }}
                    className={style.formItemMargin}
                >
                    {fieldDecorator(EXCEPTION_FORM_FIELDS.RECEIPT_ID, {
                        initialValue: receipts.isQC
                            ? exception?.qcId
                            : exception?.receiptId,
                    })(
                        <Select
                            className={style.selectLayoutStyle}
                            placeholder="Select receipt"
                            getPopupContainer={(triggerNode: HTMLElement) =>
                                triggerNode.parentNode as HTMLElement
                            }
                            disabled={
                                (exception?.type === ExceptionFailureType.OT &&
                                    exception?.creator ===
                                        ExceptionCreator.auto) ||
                                !!(
                                    exception?.correctiveActionRequestId &&
                                    !isQCManager
                                )
                            }
                        >
                            {receipts.receipts?.map((option, index: number) => (
                                <Option value={option.id} key={index}>
                                    <Row>
                                        <Col span={20}>
                                            <Text>{option.name}</Text>
                                        </Col>
                                        <Col
                                            span={3}
                                            style={{ marginLeft: '20px' }}
                                        >
                                            <StatusTag
                                                tag={getStatusTag(option.state)}
                                            />
                                        </Col>
                                    </Row>
                                </Option>
                            ))}
                        </Select>,
                    )}
                </Form.Item>
            </Box>
            <Box>
                <EnterOutlined className={componentStyle.enterIconStyle} />
                <Text className={componentStyle.receiptTitleStyle}>
                    Delivery order:
                </Text>
                <Form.Item
                    style={{ width: '81%', marginLeft: '9px' }}
                    className={style.formItemMargin}
                >
                    {fieldDecorator(EXCEPTION_FORM_FIELDS.SHIPMENT_ID, {
                        initialValue: exception?.shipmentId,
                    })(
                        <Select
                            className={style.selectLayoutStyle}
                            placeholder="Select delivery order"
                            getPopupContainer={(triggerNode: HTMLElement) =>
                                triggerNode.parentNode as HTMLElement
                            }
                            disabled={
                                (exception?.type === ExceptionFailureType.OT &&
                                    exception?.creator ===
                                        ExceptionCreator.auto) ||
                                !!(
                                    exception?.correctiveActionRequestId &&
                                    !isQCManager
                                )
                            }
                        >
                            {deliveryOrders?.map((option, index: number) => (
                                <Option value={option.id} key={index}>
                                    <Row>
                                        <Col span={20}>
                                            <Text>
                                                {`${option.name}${
                                                    option.committedCompletionDate
                                                        ? `-${formatDate(
                                                              option.committedCompletionDate,
                                                              false,
                                                              true,
                                                          )}`
                                                        : ''
                                                }`}
                                            </Text>
                                        </Col>
                                        <Col
                                            span={3}
                                            style={{ marginLeft: '16px' }}
                                        >
                                            <StatusTag
                                                tag={getStatusTag(option.state)}
                                            />
                                        </Col>
                                    </Row>
                                </Option>
                            ))}
                        </Select>,
                    )}
                </Form.Item>
            </Box>
        </>
    )
}

export const AffectedLinesFormField: FC<
    PropsWithChildren<AffectedLinesPropsType>
> = props => {
    const {
        fieldDecorator,
        setFieldsValue,
        exception,
        parts,
        partsLoading,
        partsWithQty,
        setPartsWithQty,
    } = props

    const isQCManager = useSelector(isUserQCManager)

    useEffect(() => {
        if (!partsLoading && parts?.length) {
            const initialAffetedQty = parts?.map(part => {
                const existingPart = exception?.exceptionFailureParts?.find(
                    extPart => extPart.erpProductId === Number(part.productId),
                )

                const newPartDetails = {
                    productId: Number(part.productId),
                    partName: part.partName,
                    lines: part.lines,
                    processes: part.processes,
                    orderedQty: part.orderedQty,
                }

                if (!existingPart) {
                    return {
                        ...newPartDetails,
                        failedQuantity: 0,
                        isChecked: false,
                    }
                }

                return {
                    ...newPartDetails,
                    failedQuantity: existingPart?.failedQuantity,
                    isChecked: true,
                }
            })

            setPartsWithQty(initialAffetedQty || [])
        }
    }, [parts])

    const setAllItemSelectedToQtyAffected = (allSelected: boolean) => {
        const formattedQtyArray = partsWithQty?.map(part => {
            return { ...part, isChecked: allSelected }
        })

        setPartsWithQty(formattedQtyArray || [])
    }

    const allSelectedParts = partsWithQty?.filter(part => part.isChecked)

    const handlePartChange = (
        productId: number,
        failedQuantity: number,
        isChecked: boolean,
    ) => {
        const updatedParts = partsWithQty?.map(part => {
            if (part.productId === productId) {
                part.failedQuantity = failedQuantity
                part.isChecked = isChecked
            }
            return part
        })

        setPartsWithQty(updatedParts)
    }

    return (
        <div>
            <Text className={style.woLineTitleStyle}>
                Select job request line items affected
            </Text>
            <Row className={style.checkBoxLayoutStyle}>
                <Form.Item className={style.allItemsStyle}>
                    {fieldDecorator(EXCEPTION_FORM_FIELDS.ALL_PARTS, {
                        valuePropName: 'checked',
                        initialValue: allSelectedParts.length === parts?.length,
                    })(
                        <Checkbox
                            id="allItems"
                            onChange={evt => {
                                setAllItemSelectedToQtyAffected(
                                    evt.target.checked,
                                )
                            }}
                            disabled={
                                !!(
                                    exception?.correctiveActionRequestId &&
                                    !isQCManager
                                )
                            }
                        >
                            All line items
                        </Checkbox>,
                    )}
                </Form.Item>
                <Divider style={{ margin: '4px 0px' }} />
                <Box justifyContent="center">
                    <Spin spinning={partsLoading} size="small" />
                    {!partsLoading && (
                        <Row style={{ margin: '2px 0px', marginLeft: '8px' }}>
                            {partsWithQty?.map(option => {
                                return (
                                    <Col
                                        key={option.productId}
                                        span={24}
                                        style={{ marginTop: '4px' }}
                                    >
                                        <Checkbox
                                            data-test-target={`${option.partName}-filter-checkbox`}
                                            checked={option.isChecked}
                                            onChange={evt => {
                                                if (!evt.target.checked) {
                                                    setFieldsValue({
                                                        allItems: false,
                                                    })
                                                }

                                                handlePartChange(
                                                    option.productId,
                                                    option.failedQuantity,
                                                    evt.target.checked,
                                                )
                                            }}
                                            disabled={
                                                !!(
                                                    exception?.correctiveActionRequestId &&
                                                    !isQCManager
                                                )
                                            }
                                        >
                                            <span
                                                onClick={evt => {
                                                    evt.preventDefault()
                                                    evt.stopPropagation()
                                                }}
                                            >
                                                <InputNumber
                                                    type="number"
                                                    defaultValue={
                                                        option.failedQuantity
                                                    }
                                                    min={0}
                                                    max={option.orderedQty}
                                                    onChange={value => {
                                                        handlePartChange(
                                                            option.productId,
                                                            value || 0,
                                                            true,
                                                        )
                                                    }}
                                                    style={{
                                                        width: '80px',
                                                    }}
                                                    disabled={
                                                        !!(
                                                            exception?.correctiveActionRequestId &&
                                                            !isQCManager
                                                        )
                                                    }
                                                />
                                                {` / ${option.orderedQty} ${option.partName} (${option.processes}) ${option.lines}`}
                                            </span>
                                        </Checkbox>
                                    </Col>
                                )
                            })}
                        </Row>
                    )}
                </Box>
            </Row>
        </div>
    )
}
